import { Instance, SnapshotOut, types, flow, getRoot } from "mobx-state-tree"
import { User, RootStoreModel } from ".."
import { OrderModel, OrderSnapshot } from "../order/order"
import {
  AttachTakeOrderData,
  ContactData,
  Event,
  EventNames,
  FLAG_ACK,
  FLAG_REPLY,
  FriendCommonData,
  PendingOrderData,
  TakeOrderData,
  TakeOrderStatusData,
} from "../../proto/nbchat-proto"

/**
 * 訂單 store
 */
export const OrderStoreModel = types
  .model("OrderStore")
  .props({
    orders: types.optional(types.map(OrderModel), {}),
  })
  .volatile<{
    tradingRoomCreatedData: AttachTakeOrderData | false
    isTradingRoomAlertShowing: boolean
  }>(() => ({
    tradingRoomCreatedData: false,
    isTradingRoomAlertShowing: false,
  }))
  .views((self) => ({
    get ol() {
      return Array.from(self.orders.values()).sort((a, b) => {
        return a.created > b.created ? -1 : 1
      })
    },
    get orderList() {
      return this.ol.filter((order) => order.stock >= 100)
    },
    get myOrderList() {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      return this.orderList.filter(({ user_id }) => user_id === userStore.currentUser.id)
    },
  }))
  .actions((self) => {
    const { apiStore, authStore } = getRoot<Instance<typeof RootStoreModel>>(self)
    const adaptor = (data: OrderSnapshot) => {
      return {
        ...data,
        created: new Date(data.created as any).valueOf(),
        amount: Number(data.amount),
      }
    }
    const handleOrderCreate = (data: OrderSnapshot) => {
      data.avatar = data.avatar || ""
      return self.orders.put(adaptor(data) as any)
    }
    const onOrderCreated = ({ data }: Event<OrderSnapshot>) => {
      return handleOrderCreate(data)
    }
    const handleOrderUpdate = (data: OrderSnapshot) => {
      const order = self.orders.get(data.order_no)
      if (order) {
        order.update(adaptor(data))
      }
      return order
    }
    const onOrderUpdated = ({ data }: Event<OrderSnapshot>) => {
      return handleOrderUpdate(data)
    }
    const handleOrderDelete = (data: Partial<OrderSnapshot>) => {
      return self.orders.delete(data.order_no)
    }
    const onOrderDeleted = ({ data }: Event<Partial<OrderSnapshot>>) => {
      return handleOrderDelete(data)
    }

    const setIsTradingRoomAlertShowing = (v: boolean) => {
      self.isTradingRoomAlertShowing = v
    }
    const setTradingRoomCreatedData = (data: AttachTakeOrderData | false) => {
      self.tradingRoomCreatedData = data
    }
    const handleTradingRoomCreated = (data: AttachTakeOrderData) => {
      setTradingRoomCreatedData(data)
    }
    const onTradingRoomCreated = ({ data }: Event<AttachTakeOrderData>) => {
      return handleTradingRoomCreated(data)
    }
    const setOrderList = (data) => {
      ;(Array.isArray(data) ? data : data?.data || []).forEach(handleOrderCreate)
    }
    const loadOrderlist = flow(function* () {
      const token = authStore.getToken
      try {
        const res = yield apiStore.getPendingOrders({ token })
        self.orders.clear()
        setOrderList(res?.data != null ? res.data : res)
      } catch (e) {
        // ignore
      }
    })
    const loadMyOrders = async (isSettled) => {
      const token = authStore.getToken
      try {
        const res = await apiStore.getMyOrders({ token, isSettled })
        return res
      } catch (e) {
        console.warn(e.message)
      }
    }
    const loadMyOrderDetail = async (id: string | number) => {
      const token = authStore.getToken
      try {
        const res = await apiStore.getMyOrderDetail({ token, id })
        return res
      } catch (e) {
        console.warn(e.message)
      }
    }
    const publicPendingOrderNew = (data: PendingOrderData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_NEW, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }
    const publicPendingUpdated = (data: PendingOrderData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_UPDATED, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }
    const publicPendingOrderCancel = (data: PendingOrderData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_CANCEL, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }
    const publicPendingOrderDetails = (data: PendingOrderData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_DETAILS, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }
    const publicTakeOrderNew = (data: TakeOrderData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<TakeOrderData>(EventNames.TAKE_ORDER_NEW, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }
    const publishTakeOrderStatus = (
      name: EventNames,
      data: TakeOrderStatusData,
      onData?: any,
      onError?: any
    ) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<TakeOrderStatusData>(name, data)
      eventStore.publish(evt, {
        flags: onData || onError ? FLAG_REPLY : FLAG_ACK,
        onData,
        onError,
      })
    }
    const publicTakeOrderCancel = (data: TakeOrderStatusData, onData?, onError?) =>
      publishTakeOrderStatus(EventNames.TAKE_ORDER_CANCEL, data, onData, onError)
    const publicTakeOrderApprove = (data: TakeOrderStatusData, onData?, onError?) =>
      publishTakeOrderStatus(EventNames.TAKE_ORDER_APPROVE, data, onData, onError)
    const publicTakeOrderPay = (data: TakeOrderStatusData, onData?, onError?) =>
      publishTakeOrderStatus(EventNames.TAKE_ORDER_PAY, data, onData, onError)
    const publicTakeOrderDispute = (data: TakeOrderStatusData, onData?, onError?) =>
      publishTakeOrderStatus(EventNames.TAKE_ORDER_DISPUTE, data, onData, onError)
    const publicTakeOrderSettle = (data: TakeOrderStatusData, onData?, onError?) =>
      publishTakeOrderStatus(EventNames.TAKE_ORDER_SETTLE, data, onData, onError)
    const onContactCs = flow(function* (onData: (v: User) => void, onError: (v: Error) => void) {
      const { eventStore, contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const res = yield apiStore.getForceCs({
        token: authStore.getToken,
      })
      const cs_account = res?.cs_account || res?.data?.cs_account
      const csContact = contactStore.byUsername(cs_account)
      if (!csContact) {
        const evt = new Event<FriendCommonData>(EventNames.FRIEND_ADD, {
          username: cs_account,
        })
        eventStore.publish(evt, {
          flags: FLAG_REPLY,
          onData: (data: ContactData) => {
            contactStore.onContactCreated({ data } as any)
            const created = contactStore.byUsername(cs_account)
            setTimeout(() => {
              onData(created?.user)
            })
          },
          onError,
        })
      } else {
        setTimeout(() => {
          onData(csContact.user)
        })
      }
    })
    const onContact = (account: string, onData: (v: User) => void) => {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const contact = chatStore.byUsername?.(account)
      setTimeout(() => {
        onData(contact)
      })
    }
    return {
      onContact,
      onContactCs,
      onOrderCreated,
      onOrderUpdated,
      onOrderDeleted,
      onTradingRoomCreated,
      setIsTradingRoomAlertShowing,
      setTradingRoomCreatedData,
      handleOrderCreate,
      handleOrderUpdate,
      handleOrderDelete,
      handleTradingRoomCreated,
      loadOrderlist,
      loadMyOrders,
      loadMyOrderDetail,
      publicPendingOrderNew,
      publicPendingUpdated,
      publicPendingOrderCancel,
      publicPendingOrderDetails,
      publicTakeOrderNew,
      publicTakeOrderCancel,
      publicTakeOrderApprove,
      publicTakeOrderPay,
      publicTakeOrderDispute,
      publicTakeOrderSettle,
    }
  })

type OrderStoreType = Instance<typeof OrderStoreModel>
export interface OrderStore extends OrderStoreType {}
type OrderStoreSnapshotType = SnapshotOut<typeof OrderStoreModel>
export interface OrderStoreSnapshot extends OrderStoreSnapshotType {}
export const createOrderStoreDefaultModel = () => types.optional(OrderStoreModel, {})
